<template>
  <div class="test-create-container page-container bg-gradient-soft">
    <div class="container-padded py-5">
      <div class="d-flex justify-content-between align-items-center mb-5 animate__animated animate__fadeIn">
        <h1 class="mb-0 display-heading with-highlight">
          <span class="icon-circle icon-circle-primary me-3"><i class="fas fa-plus-circle"></i></span> 
          Neuen Test erstellen
        </h1>
        <router-link to="/tests" class="btn btn-outline-secondary btn-with-icon btn-hover-effect">
          <span class="icon me-2"><i class="fas fa-arrow-left"></i></span> Zurück zur Testliste
        </router-link>
      </div>
      
      <div class="row">
        <div class="col-12">
          <Card animateIn="fade-in-up" class="card-modern card-with-form shadow-lg">
            <template v-slot:header>
              <h4 class="card-title mb-0"><i class="fas fa-clipboard-list me-2"></i> Test-Informationen</h4>
            </template>
            
            <form @submit.prevent="saveTest" class="form-spaced py-4">
              <div class="mb-4">
                <label for="testName" class="form-label label-emphasized">
                  <span class="icon icon-primary me-2"><i class="fas fa-pen"></i></span>Test-Name
                </label>
                <input 
                  type="text" 
                  class="form-control form-control-lg with-focus-effect" 
                  id="testName" 
                  v-model="test.name" 
                  placeholder="z.B. Programmierkenntnisse Test"
                  required
                  :class="{ 'is-invalid': errors.name }"
                >
                <div class="invalid-feedback with-icon" v-if="errors.name">
                  <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                  {{ errors.name }}
                </div>
              </div>
              
              <div class="mb-4">
                <label for="testDescription" class="form-label label-emphasized">
                  <span class="icon icon-info me-2"><i class="fas fa-info-circle"></i></span>Beschreibung
                </label>
                <textarea 
                  class="form-control with-focus-effect" 
                  id="testDescription" 
                  v-model="test.description" 
                  placeholder="Fügen Sie eine Beschreibung für den Test hinzu"
                  rows="3"
                  :class="{ 'is-invalid': errors.description }"
                ></textarea>
                <div class="form-text">Diese Beschreibung ist nur für Sie sichtbar und hilft bei der Organisation Ihrer Tests.</div>
                <div class="invalid-feedback with-icon" v-if="errors.description">
                  <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                  {{ errors.description }}
                </div>
              </div>
              
              <!-- Fragengruppen-Auswahl -->
              <div class="mb-4">
                <label class="form-label fw-bold">
                  <span class="icon icon-primary me-2"><i class="fas fa-layer-group"></i></span>
                  Fragengruppen auswählen
                </label>
                <div class="alert alert-info mb-3">
                  <i class="fas fa-info-circle me-2"></i>
                  <strong>Hinweis:</strong> Wählen Sie Fragengruppen aus, deren Fragen in diesem Test verwendet werden sollen.
                </div>
                
                <div v-if="questionGroupsLoading" class="text-center py-3">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Wird geladen...</span>
                  </div>
                  <p class="mt-2 text-muted">Fragengruppen werden geladen...</p>
                </div>
                
                <div v-else-if="questionGroups.length === 0" class="alert alert-warning">
                  <i class="fas fa-exclamation-triangle me-2"></i>
                  <strong>Keine Fragengruppen verfügbar.</strong> 
                  <router-link to="/question-groups" class="alert-link">Erstellen Sie zuerst Fragengruppen</router-link>.
                </div>
                
                <div v-else class="question-groups-selection">
                  <div v-for="group in questionGroups" :key="group.id" class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :id="`group_${group.id}`" 
                      :value="group.id" 
                      v-model="selectedQuestionGroups"
                      @change="onQuestionGroupsChange"
                    >
                    <label class="form-check-label d-flex justify-content-between align-items-center" :for="`group_${group.id}`">
                      <span>
                        <span class="badge me-2" :style="{ backgroundColor: group.color, color: 'white' }">
                          {{ group.name }}
                        </span>
                        <small class="text-muted">{{ group.description }}</small>
                      </span>
                      <span class="badge bg-secondary">{{ group.questions_count }} Fragen</span>
                    </label>
                  </div>
                </div>
              </div>

              <div class="mb-4">
                <div class="switch-card mb-3">
                  <div class="form-check form-switch">
                    <input 
                      class="form-check-input switch-lg switch-primary" 
                      type="checkbox" 
                      id="testActive" 
                      v-model="test.active"
                    >
                    <label class="form-check-label fw-medium" for="testActive">
                      <span class="icon icon-success me-2"><i class="fas fa-check-circle"></i></span>
                      Test aktiv
                    </label>
                  </div>
                  <div class="form-text ms-5 ps-2">
                    Wenn aktiviert, können Teilnehmer diesen Test ausfüllen.
                  </div>
                </div>
                
                <div class="switch-card">
                  <div class="form-check form-switch">
                    <input 
                      class="form-check-input switch-lg switch-primary" 
                      type="checkbox" 
                      id="showResults" 
                      v-model="test.show_results"
                    >
                    <label class="form-check-label fw-medium" for="showResults">
                      <span class="icon icon-info me-2"><i class="fas fa-chart-pie"></i></span>
                      Ergebnisse anzeigen
                    </label>
                  </div>
                  <div class="form-text ms-5 ps-2">
                    Wenn aktiviert, werden den Teilnehmern nach Abschluss des Tests ihre Ergebnisse angezeigt.
                  </div>
                </div>
              </div>
              
              <div class="d-flex justify-content-between mt-5">
                <button type="submit" class="btn btn-primary btn-lg btn-with-icon btn-glow-effect" :disabled="loading">
                  <span class="icon me-2" v-if="!loading"><i class="fas fa-save"></i></span>
                  <span class="spinner-border spinner-border-sm me-2" v-else role="status"></span>
                  {{ loading ? 'Wird gespeichert...' : 'Test erstellen' }}
                </button>
              </div>
            </form>
          </Card>
          
          <div class="mt-5" v-if="test.id">
            <Card animateIn="fade-in-up" class="card-modern card-with-table shadow-lg" style="animation-delay: 0.2s">
              <template v-slot:header>
                <div class="d-flex justify-content-between align-items-center">
                  <h4 class="card-title mb-0">
                    <i class="fas fa-question-circle me-2"></i> Fragen verwalten
                  </h4>
                  <div class="btn-group">
                    <button 
                      class="btn btn-primary btn-with-icon btn-hover-effect" 
                      @click="addQuestion('multiple_choice')"
                    >
                      <span class="icon me-2"><i class="fas fa-list-ul"></i></span>
                      Multiple Choice
                    </button>
                    <button 
                      class="btn btn-info btn-with-icon btn-hover-effect" 
                      @click="addQuestion('open')"
                    >
                      <span class="icon me-2"><i class="fas fa-font"></i></span>
                      Offene Frage
                    </button>
                  </div>
                </div>
              </template>
              
              <div v-if="questionsLoading" class="text-center py-5 content-loading card-loading-state">
                <div class="spinner-container mb-3">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Wird geladen...</span>
                  </div>
                </div>
                <p class="text-muted">Fragen werden geladen...</p>
              </div>
              
              <div v-else-if="questions.length === 0" class="text-center py-5 content-empty card-empty-state">
                <div class="empty-state-icon mb-4">
                  <i class="fas fa-question-circle"></i>
                </div>
                <h5 class="empty-state-title mb-3">Noch keine Fragen hinzugefügt</h5>
                <p class="text-muted mb-4">Fügen Sie Fragen hinzu, um Ihren Test zu erstellen.</p>
                <div class="d-flex justify-content-center gap-3">
                  <button 
                    class="btn btn-primary btn-with-icon btn-hover-effect" 
                    @click="addQuestion('multiple_choice')"
                  >
                    <span class="icon me-2"><i class="fas fa-list-ul"></i></span>
                    Multiple Choice
                  </button>
                  <button 
                    class="btn btn-info btn-with-icon btn-hover-effect" 
                    @click="addQuestion('open')"
                  >
                    <span class="icon me-2"><i class="fas fa-font"></i></span>
                    Offene Frage
                  </button>
                </div>
              </div>
              
              <div v-else>
                <div class="alert alert-info mb-4 with-icon alert-modern">
                  <span class="icon icon-info me-2"><i class="fas fa-info-circle"></i></span>
                  <span>Ziehen Sie die Fragen, um die Reihenfolge zu ändern. Klicken Sie auf eine Frage, um sie zu bearbeiten.</span>
                </div>
                
                <draggable 
                  v-model="questions" 
                  handle=".drag-handle"
                  ghost-class="drag-ghost"
                  animation="300"
                  @end="saveQuestionsOrder"
                  class="questions-list-modern"
                >
                  <transition-group name="question-list">
                    <div 
                      v-for="(question, index) in questions" 
                      :key="question.id"
                      class="question-card"
                      :class="[
                        { 'question-card-primary': question.type === 'multiple_choice' },
                        { 'question-card-info': question.type === 'open' }
                      ]"
                      @click="editQuestion(question)"
                    >
                      <div class="row align-items-center">
                        <div class="col-auto drag-handle">
                          <div class="drag-handle-icon">
                            <i class="fas fa-grip-vertical"></i>
                          </div>
                        </div>
                        <div class="col-auto">
                          <div class="question-number-badge">{{ index + 1 }}</div>
                        </div>
                        <div class="col">
                          <div class="d-flex flex-wrap align-items-center mb-2">
                            <h5 class="question-title mb-0 me-3">{{ question.text }}</h5>
                            <span 
                              class="question-type-badge"
                              :class="question.type === 'multiple_choice' ? 'badge-primary' : 'badge-info'"
                            >
                              {{ question.type === 'multiple_choice' ? 'Multiple Choice' : 'Offene Frage' }}
                            </span>
                          </div>
                          
                          <div v-if="question.type === 'multiple_choice' && question.options?.length > 0" class="question-options">
                            <div class="options-preview">
                              <div 
                                v-for="option in question.options.slice(0, 3)" 
                                :key="option.id" 
                                class="option-preview-item"
                                :class="{ 'option-correct': option.is_correct }"
                              >
                                <span class="option-icon me-2">
                                  <i :class="option.is_correct ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                                </span>
                                {{ option.text }}
                              </div>
                              <div v-if="question.options.length > 3" class="option-preview-more">
                                + {{ question.options.length - 3 }} weitere Optionen
                              </div>
                            </div>
                          </div>
                          
                          <div v-else-if="question.type === 'open'" class="question-open-info">
                            <span class="badge badge-soft-info"><i class="fas fa-pen me-1"></i> Freie Texteingabe</span>
                          </div>
                        </div>
                        <div class="col-auto">
                          <div class="question-actions">
                            <button 
                              class="btn-action btn-action-edit" 
                              @click.stop="editQuestion(question)"
                              title="Bearbeiten"
                            >
                              <i class="fas fa-edit"></i>
                            </button>
                            <button 
                              class="btn-action btn-action-delete" 
                              @click.stop="confirmDeleteQuestion(question)"
                              title="Löschen"
                            >
                              <i class="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition-group>
                </draggable>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Question Modal -->
    <b-modal 
      id="question-modal" 
      ref="questionModal"
      :title="editingQuestion.id ? 'Frage bearbeiten' : 'Neue Frage'"
      size="lg"
      hide-footer
      centered
      no-close-on-backdrop
      class="question-modal-modern"
      header-class="modal-header-modern"
      content-class="modal-content-modern"
    >
      <div class="modal-body-padded">
        <div class="mb-4">
          <label for="questionText" class="form-label label-emphasized">
            <span class="icon icon-primary me-2"><i class="fas fa-question-circle"></i></span>
            Fragetext
          </label>
          <textarea 
            class="form-control with-focus-effect" 
            id="questionText" 
            v-model="editingQuestion.text" 
            rows="3"
            required
            placeholder="Geben Sie hier Ihre Frage ein..."
          ></textarea>
        </div>
        
        <div class="mb-4" v-if="editingQuestion.type === 'multiple_choice'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <label class="form-label label-emphasized mb-0">
              <span class="icon icon-info me-2"><i class="fas fa-list-ul"></i></span>
              Antwortoptionen
            </label>
            <button 
              type="button" 
              class="btn btn-outline-primary btn-sm btn-with-icon btn-hover-effect"
              @click="addOption"
            >
              <span class="icon me-1"><i class="fas fa-plus"></i></span>
              Option hinzufügen
            </button>
          </div>
          
          <draggable 
            v-model="editingQuestion.options" 
            animation="300"
            class="options-list-modern"
            ghost-class="drag-ghost-option"
          >
            <transition-group name="option-list">
              <div 
                v-for="(option, index) in editingQuestion.options" 
                :key="option.id || index" 
                class="option-item-modern"
                :class="{ 'option-correct': option.is_correct }"
              >
                <div class="option-content">
                  <div class="option-drag-handle">
                    <i class="fas fa-grip-vertical"></i>
                  </div>
                  <div class="option-input">
                    <div class="input-group input-group-modern">
                      <div class="input-group-text bg-light border-end-0">
                        <i class="fas fa-align-left"></i>
                      </div>
                      <input 
                        type="text" 
                        class="form-control border-start-0" 
                        v-model="option.text" 
                        :placeholder="`Option ${index + 1}`"
                        required
                      >
                    </div>
                  </div>
                  <div class="option-actions">
                    <div class="form-check form-switch switch-modern">
                      <input 
                        class="form-check-input switch-success"
                        type="checkbox"
                        :id="`option-${index}`"
                        v-model="option.is_correct"
                      >
                      <label class="form-check-label" :for="`option-${index}`">Korrekt</label>
                    </div>
                    <button 
                      type="button" 
                      class="btn-action btn-action-delete"
                      @click="removeOption(index)"
                      title="Option entfernen"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </transition-group>
          </draggable>
          
          <div class="alert alert-warning mt-3 alert-modern with-icon" v-if="editingQuestion.options.length === 0">
            <span class="icon icon-warning me-2"><i class="fas fa-exclamation-triangle"></i></span>
            <span>Fügen Sie mindestens eine Antwortoption hinzu.</span>
          </div>
          
          <div class="alert alert-danger mt-3 alert-modern with-icon" v-if="noCorrectOptionSelected && editingQuestion.options.length > 0">
            <span class="icon icon-danger me-2"><i class="fas fa-exclamation-circle"></i></span>
            <span>Bitte markieren Sie mindestens eine Option als korrekt.</span>
          </div>
        </div>
        
        <div class="mb-4" v-if="editingQuestion.type === 'open'">
          <label for="explanationText" class="form-label label-emphasized">
            <span class="icon icon-success me-2"><i class="fas fa-check-double"></i></span>
            Erklärung/Musterantwort (optional)
          </label>
          <textarea 
            class="form-control with-focus-effect" 
            id="explanationText" 
            v-model="editingQuestion.explanation" 
            rows="3"
            placeholder="Geben Sie eine Musterantwort oder Erklärung ein"
          ></textarea>
          <div class="form-text">
            Diese Erklärung dient als Referenz für die Bewertung und wird den Teilnehmern nicht angezeigt.
          </div>
        </div>
        
        <div class="modal-actions mt-5">
          <button 
            type="button" 
            class="btn btn-outline-secondary btn-with-icon btn-hover-effect"
            @click="$refs.questionModal.hide()"
          >
            <span class="icon me-2"><i class="fas fa-times"></i></span>
            Abbrechen
          </button>
          <button 
            type="button" 
            class="btn btn-primary btn-with-icon btn-glow-effect"
            @click="saveQuestion"
            :disabled="savingQuestion"
          >
            <span class="icon me-2" v-if="!savingQuestion"><i class="fas fa-save"></i></span>
            <span class="spinner-border spinner-border-sm me-2" v-else role="status"></span>
            {{ savingQuestion ? 'Wird gespeichert...' : 'Frage speichern' }}
          </button>
        </div>
      </div>
    </b-modal>
    
    <!-- Delete Confirmation Modal -->
    <b-modal
      id="delete-question-modal"
      ref="deleteModal"
      title="Frage löschen"
      ok-title="Löschen"
      cancel-title="Abbrechen"
      ok-variant="danger"
      @ok="deleteQuestion"
      centered
      header-class="modal-header-modern"
      content-class="modal-content-modern"
    >
      <div class="modal-body-padded text-center">
        <div class="delete-icon mb-4">
          <i class="fas fa-trash-alt"></i>
        </div>
        <h5 class="mb-4">Sind Sie sicher, dass Sie diese Frage löschen möchten?</h5>
        <p class="text-danger with-icon mb-0">
          <span class="icon icon-danger me-2"><i class="fas fa-exclamation-triangle"></i></span>
          Diese Aktion kann nicht rückgängig gemacht werden!
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Card from '@/components/ui/Card.vue';
import draggable from 'vuedraggable';
import { v4 as uuidv4 } from 'uuid';
import { useTestsStore } from '@/stores/tests';
import { useCompaniesStore } from '@/stores/companies';
import { useQuestionGroupsStore } from '@/stores/questionGroups';
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';

export default {
  name: 'TestCreate',
  components: {
    Card,
    draggable
  },
  setup() {
    const testsStore = useTestsStore();
    const companiesStore = useCompaniesStore();
    const questionGroupsStore = useQuestionGroupsStore();
    
    return {
      testsStore,
      companiesStore,
      questionGroupsStore
    };
  },
  data() {
    return {
      loading: false,
      questionsLoading: false,
      savingQuestion: false,
      test: {
        id: null,
        name: '',
        description: '',
        company_id: '',
        max_points: 100,
        active: true,
        unique_id: '',
        show_results: true
      },
      questions: [],
      editingQuestion: {
        id: null,
        text: '',
        type: 'multiple_choice',
        options: [],
        explanation: ''
      },
      questionToDelete: null,
      errors: {},
      noCorrectOptionSelected: false,
      submitted: false,
      testCreated: false,
      testShareUrl: '',
      defaultPointsPerQuestion: 10,
      questionGroups: [],
      questionGroupsLoading: false,
      selectedQuestionGroups: []
    };
  },
  computed: {
    isNewTest() {
      return !this.test.id;
    },
    testId() {
      return this.$route.params.id;
    },
    totalPoints() {
      if (!this.test.questions || this.test.questions.length === 0) {
        return 0;
      }
      
      return this.test.questions.reduce((total, question) => {
        return total + (question.points || this.defaultPointsPerQuestion);
      }, 0);
    },
    companies() {
      return this.companiesStore.getAllCompanies;
    }
  },
  methods: {
    async saveTest() {
      this.submitted = true;
      this.loading = true;
      this.errors = {};
      
      // Validierung
      let isValid = true;
      
      if (!this.test.name.trim()) {
        this.errors.name = 'Bitte geben Sie einen Namen für den Test ein';
        isValid = false;
      }
      
      if (!this.test.max_points || this.test.max_points <= 0) {
        this.errors.max_points = 'Bitte geben Sie eine gültige maximale Punktzahl ein';
        isValid = false;
      }
      
      if (!isValid) {
        this.loading = false;
        this.$toast.error('Bitte korrigieren Sie die markierten Felder');
        return;
      }
      
      try {
        if (this.isNewTest) {
          // Generiere eine eindeutige ID für den Test
          this.test.unique_id = this.generateUniqueId();
          
          // Fragengruppen mitgeben
          const payload = {
            ...this.test,
            question_group_ids: this.selectedQuestionGroups
          };

          const createdTest = await this.testsStore.createTest(payload);
          this.test.id = createdTest.id;
          
          // Erstelle die Share-URL
          this.testShareUrl = `${window.location.origin}/test/${this.test.unique_id}`;
          
          // Nach dem Erstellen zur Edit-Seite navigieren
          this.$router.push(`/tests/${this.test.id}/edit`);
          
          this.$toast.success('Test wurde erfolgreich erstellt.');
        } else {
          const payload = {
            ...this.test,
            question_group_ids: this.selectedQuestionGroups
          };
          await this.testsStore.updateTestData(this.test.id, payload);
          
          this.$toast.success('Test wurde erfolgreich aktualisiert.');
        }
      } catch (error) {
        this.$toast.error('Fehler beim Speichern des Tests');
        console.error(error);
        
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        }
      } finally {
        this.loading = false;
      }
    },
    async loadTest() {
      if (!this.testId) return;
      
      this.loading = true;
      
      try {
        const test = await this.testsStore.fetchTest(this.testId);
        this.test = { ...test };
        
        await this.loadQuestions();
      } catch (error) {
        this.$toast.error('Fehler beim Laden des Tests.');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async loadQuestions() {
      if (!this.test.id) return;
      
      this.questionsLoading = true;
      
      try {
        const questions = await this.testsStore.fetchQuestions(this.test.id);
        this.questions = [...questions];
      } catch (error) {
        this.$toast.error('Fehler beim Laden der Fragen.');
        console.error(error);
      } finally {
        this.questionsLoading = false;
      }
    },
    addQuestion(type) {
      this.editingQuestion = {
        id: null,
        text: '',
        type: type,
        options: type === 'multiple_choice' ? [
          { id: uuidv4(), text: '', is_correct: false },
          { id: uuidv4(), text: '', is_correct: false }
        ] : [],
        explanation: ''
      };
      
      this.$refs.questionModal.show();
    },
    editQuestion(question) {
      this.editingQuestion = JSON.parse(JSON.stringify(question));
      this.$refs.questionModal.show();
    },
    addOption() {
      this.editingQuestion.options.push({
        id: uuidv4(),
        text: '',
        is_correct: false
      });
    },
    removeOption(index) {
      this.editingQuestion.options.splice(index, 1);
    },
    async saveQuestion() {
      this.savingQuestion = true;
      this.noCorrectOptionSelected = false;
      
      // Validierung für Multiple Choice
      if (this.editingQuestion.type === 'multiple_choice') {
        if (this.editingQuestion.options.length === 0) {
          this.$toast.error('Bitte fügen Sie mindestens eine Antwortoption hinzu.');
          this.savingQuestion = false;
          return;
        }
        
        const hasCorrectOption = this.editingQuestion.options.some(option => option.is_correct);
        if (!hasCorrectOption) {
          this.noCorrectOptionSelected = true;
          this.$toast.error('Bitte markieren Sie mindestens eine Option als korrekt.');
          this.savingQuestion = false;
          return;
        }
      }
      
      try {
        if (this.editingQuestion.id) {
          // Frage aktualisieren
          await this.testsStore.updateQuestion(
            this.test.id,
            this.editingQuestion.id,
            this.editingQuestion
          );
          
          // Frage in der lokalen Liste aktualisieren
          const index = this.questions.findIndex(q => q.id === this.editingQuestion.id);
          if (index !== -1) {
            this.questions.splice(index, 1, { ...this.editingQuestion });
          }
          
        } else {
          // Neue Frage erstellen
          const createdQuestion = await this.testsStore.createQuestion(
            this.test.id,
            {
              ...this.editingQuestion,
              position: this.questions.length
            }
          );
          
          // Neue Frage zur lokalen Liste hinzufügen
          this.questions.push(createdQuestion);
          
          this.$toast.success('Frage wurde erfolgreich erstellt.');
        }
        
        this.$refs.questionModal.hide();
      } catch (error) {
        this.$toast.error('Fehler beim Speichern der Frage.');
        console.error(error);
      } finally {
        this.savingQuestion = false;
      }
    },
    confirmDeleteQuestion(question) {
      this.questionToDelete = question;
      this.$refs.deleteModal.show();
    },
    async deleteQuestion() {
      if (!this.questionToDelete) return;
      
      try {
        await this.testsStore.deleteQuestion(this.questionToDelete.id);
        
        // Frage aus der lokalen Liste entfernen
        const index = this.questions.findIndex(q => q.id === this.questionToDelete.id);
        if (index !== -1) {
          this.questions.splice(index, 1);
        }
        
        this.$toast.success('Frage erfolgreich entfernt');
        this.questionToDelete = null;
      } catch (error) {
        this.$toast.error('Frage konnte nicht gelöscht werden');
        console.error(error);
      }
    },
    async saveQuestionsOrder() {
      try {
        const orderedIds = this.questions.map((question, index) => ({
          id: question.id,
          position: index
        }));
        
        await this.testsStore.updateQuestionsOrder(this.test.id, orderedIds);
        
        this.$toast.success('Reihenfolge der Fragen erfolgreich aktualisiert.');
      } catch (error) {
        this.$toast.error('Fehler beim Aktualisieren der Reihenfolge.');
        console.error(error);
      }
    },
    generateUniqueId() {
      // Generiere eine zufällige 10-stellige alphanumerische ID
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let uniqueId = '';
      for (let i = 0; i < 10; i++) {
        uniqueId += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return uniqueId;
    },
    copyToClipboard() {
      const copyText = document.getElementById('testShareUrl');
      copyText.select();
      document.execCommand('copy');
    },
    
    // Fragengruppen-Methoden
    async loadQuestionGroups() {
      this.questionGroupsLoading = true;
      try {
        await this.questionGroupsStore.fetchQuestionGroups();
        this.questionGroups = this.questionGroupsStore.getAllQuestionGroups;
      } catch (error) {
        console.warn('Fragengruppen konnten nicht geladen werden:', error);
        this.questionGroups = [];
      } finally {
        this.questionGroupsLoading = false;
      }
    },
    
    onQuestionGroupsChange() {
      // Hier können später Fragen nach Gruppen geladen werden
      console.log('Ausgewählte Fragengruppen:', this.selectedQuestionGroups);
    },
    
    async loadQuestionsByGroups() {
      if (this.selectedQuestionGroups.length === 0) {
        this.questions = [];
        return;
      }
      
      try {
        const response = await this.$http.post(`/api/tests/${this.test.id}/questions/by-groups`, {
          group_ids: this.selectedQuestionGroups
        });
        this.questions = response.data;
      } catch (error) {
        console.error('Fehler beim Laden der Fragen nach Gruppen:', error);
        this.$toast.error('Fragen konnten nicht geladen werden');
      }
    },
    
    // Zeige kurze Bestätigung
    showCopySuccess() {
      this.$toast.success('Link in die Zwischenablage kopiert!', {
        position: 'top-right',
        duration: 3000,
        dismissible: true
      });
    }
  },
  created() {
    this.companiesStore.fetchCompanies();
    this.loadQuestionGroups();
    
    if (this.testId) {
      this.loadTest();
    }
  }
};
</script>

<style lang="scss" scoped>
.test-create-container {
  --primary-gradient: linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-primary-dark) 100%);
  --secondary-gradient: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  --card-radius: 1rem;
  --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
  --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --border-width: 1px;
  --header-height: 4rem;
  
  background-color: var(--bs-gray-100);
  min-height: 100vh;
  padding-bottom: 3rem;
}

.display-heading {
  font-size: 2.25rem;
  font-weight: 700;
  color: var(--bs-gray-900);
  letter-spacing: -0.02em;
  
  &.with-highlight {
    position: relative;
    padding-bottom: 0.5rem;
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 4rem;
      height: 0.25rem;
      background: var(--primary-gradient);
      border-radius: 1rem;
    }
  }
}

.icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: var(--bs-gray-100);
  color: var(--bs-primary);
  transition: var(--transition-base);
  border: 2px solid var(--bs-primary);
  
  &.icon-circle-primary {
    background: var(--bs-white);
    color: var(--bs-primary);
    border: 2px solid var(--bs-primary);
    box-shadow: var(--shadow-sm);
    
    &:hover {
      background: var(--bs-primary);
      color: var(--bs-white);
    }
  }
}

.card-modern {
  background: white;
  border: none;
  border-radius: var(--card-radius);
  box-shadow: var(--shadow-md);
  transition: var(--transition-base);
  
  &:hover {
    box-shadow: var(--shadow-lg);
  }
  
  :deep(.card-header) {
    background: white;
    border-bottom: var(--border-width) solid var(--bs-gray-200);
    padding: 1.5rem;
    
    .card-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--bs-gray-900);
      margin: 0;
      
      i {
        color: var(--bs-primary);
        margin-right: 0.75rem;
      }
    }
  }
  
  :deep(.card-body) {
    padding: 1.5rem;
  }
}

.form-spaced {
  .form-label {
    font-weight: 600;
    color: var(--bs-gray-700);
    margin-bottom: 0.75rem;
    
    &.label-emphasized {
      display: flex;
      align-items: center;
      font-size: 1rem;
      
      .icon {
        color: var(--bs-primary);
        margin-right: 0.5rem;
      }
    }
  }
  
  .form-control {
    border: var(--border-width) solid var(--bs-gray-300);
    border-radius: 0.75rem;
    padding: 0.875rem 1rem;
    font-size: 1rem;
    transition: var(--transition-base);
    
    &:focus {
      border-color: var(--bs-primary);
      box-shadow: 0 0 0 4px rgba(var(--bs-primary-rgb), 0.1);
    }
    
    &.form-control-lg {
      font-size: 1.125rem;
      padding: 1rem 1.25rem;
    }
  }
}

.switch-card {
  background: var(--secondary-gradient);
  border-radius: 1rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
  transition: var(--transition-base);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
  
  .form-check-input {
    width: 3rem;
    height: 1.5rem;
    border-radius: 1rem;
    background-color: var(--bs-gray-400);
    border: none;
    
    &:checked {
      background-color: var(--bs-primary);
    }
    
    &:focus {
      box-shadow: 0 0 0 4px rgba(var(--bs-primary-rgb), 0.1);
    }
  }
  
  .form-check-label {
    font-weight: 500;
    color: var(--bs-gray-700);
  }
}

.btn-with-icon {
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 500;
  transition: var(--transition-base);
  
  .icon {
    margin-right: 0.5rem;
  }
  
  &.btn-primary {
    background: var(--bs-primary);
    border: 2px solid var(--bs-primary);
    color: var(--bs-white);
    box-shadow: var(--shadow-sm);
    
    &:hover {
      background: var(--bs-white);
      color: var(--bs-primary);
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }

    &:disabled {
      background: var(--bs-gray-400);
      border-color: var(--bs-gray-400);
      color: var(--bs-white);
      cursor: not-allowed;
      transform: none;
    }
  }
  
  &.btn-outline-secondary {
    background: var(--bs-white);
    border: 2px solid var(--bs-gray-300);
    color: var(--bs-gray-700);
    
    &:hover {
      border-color: var(--bs-primary);
      color: var(--bs-primary);
      background: var(--bs-white);
      transform: translateY(-2px);
      box-shadow: var(--shadow-sm);
    }
  }
}

.questions-list-modern {
  .question-card {
    background: white;
    border-radius: 1rem;
    padding: 1.5rem;
    margin-bottom: 1rem;
    box-shadow: var(--shadow-sm);
    transition: var(--transition-base);
    border-left: 4px solid var(--bs-primary);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }
    
    .question-title {
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--bs-gray-900);
      margin-bottom: 1rem;
    }
    
    .question-type-badge {
      display: inline-flex;
      align-items: center;
      padding: 0.5rem 1rem;
      border-radius: 2rem;
      font-size: 0.875rem;
      font-weight: 500;
      background: rgba(var(--bs-primary-rgb), 0.1);
      color: var(--bs-primary);
    }
    
    .option-preview-item {
      background: var(--bs-gray-100);
      border-radius: 0.75rem;
      padding: 0.75rem 1rem;
      margin-bottom: 0.5rem;
      
      &.option-correct {
        background: rgba(var(--bs-success-rgb), 0.1);
        color: var(--bs-success);
      }
    }
  }
}

.modal-modern {
  :deep(.modal-content) {
    border-radius: 1rem;
    border: none;
    box-shadow: var(--shadow-lg);
  }
  
  :deep(.modal-header) {
    background: var(--primary-gradient);
    padding: 1.5rem;
    border: none;
    
    .modal-title {
      color: white;
      font-weight: 600;
    }
  }
  
  :deep(.modal-body) {
    padding: 1.5rem;
  }
}

.options-list-modern {
  .option-item-modern {
    background: var(--bs-gray-100);
    border-radius: 0.75rem;
    padding: 1rem;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .option-input {
      flex: 1;
      
      input {
        border-radius: 0.5rem;
      }
    }
    
    .option-actions {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
  }
}

.alert-modern {
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  border: none;
  background: var(--secondary-gradient);
  
  &.alert-info {
    background: rgba(var(--bs-info-rgb), 0.1);
    color: var(--bs-info);
  }
  
  &.alert-warning {
    background: rgba(var(--bs-warning-rgb), 0.1);
    color: var(--bs-warning);
  }
  
  &.alert-danger {
    background: rgba(var(--bs-danger-rgb), 0.1);
    color: var(--bs-danger);
  }
}

.empty-state {
  text-align: center;
  padding: 3rem 1.5rem;
  
  .empty-state-icon {
    width: 5rem;
    height: 5rem;
    margin: 0 auto 1.5rem;
    background: var(--secondary-gradient);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: var(--bs-primary);
  }
  
  .empty-state-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--bs-gray-900);
    margin-bottom: 0.75rem;
  }
  
  .empty-state-text {
    color: var(--bs-gray-600);
    margin-bottom: 1.5rem;
  }
}
</style> 